import * as React from 'react';
import { Button } from 'react-bootstrap';
import {
  BloodworkAnalysis,
  FurtherConsiderations,
  PanelMarker,
  Section,
} from '../../models';
import {
  prioritizeRootCauseCategories,
  renderArrows,
  trianglesToArrows,
  referenceItemToText,
  isNumberedReferenceList,
  sortByReferenceNumber,
} from './further-considerations-section';
import { sectionHasReferencesTab } from './reference-tab-sections';
import { ViscosityInsights } from './viscosity-insights';
import { inlineHoverText } from '../../utils/hoverText';
import { stripNavTokens } from '../../utils/navToken';

// A heading + its bullets — one per underlying Consideration, so the digest
// keeps the SAME section headings the tab shows (e.g. each Insights pattern,
// each marker's root causes) instead of flattening them into one list.
export type DigestGroup = { title: string; items: string[] };
// One card per visible content tab.
export type DigestTab = { key: string; name: string; groups: DigestGroup[] };

// The content tabs the Notes digest surfaces, mirroring the visibility gates in
// tabs-component.tsx. Intentionally EXCLUDES:
//   - Reference Range (the bar graphs already cover it),
//   - Supplements (consolidated separately in SupplementsSummaryPanel),
//   - References (citation slugs that need their own resolver), and
//   - Notes (the destination we're writing into).
const CONTENT_TABS: {
  key: string;
  name: string;
  tab: string;
  show: (section: Section) => boolean;
}[] = [
  {
    key: 'insights',
    name: 'Insights',
    tab: 'Interpretation',
    show: s => [Section.RBC, Section.WBC, Section.GLUCOSE].includes(s),
  },
  {
    key: 'causes',
    name: 'Possible Root Causes',
    tab: 'Causes',
    show: s => s !== Section.RBC,
  },
  {
    key: 'testing',
    name: 'Additional Testing',
    tab: 'Testing',
    show: s => s === Section.RBC,
  },
  {
    key: 'considerations',
    name: 'Further Considerations',
    tab: 'Considerations',
    show: s => [Section.RBC, Section.WBC, Section.GLUCOSE].includes(s),
  },
  {
    key: 'symptoms',
    name: 'Signs and Symptoms',
    tab: 'Symptoms',
    show: s => s === Section.RBC,
  },
  { key: 'diet', name: 'Diet', tab: 'Diet', show: s => s === Section.RBC },
  {
    key: 'lifestyle',
    name: 'Lifestyle',
    tab: 'Lifestyle',
    show: s => s === Section.RBC,
  },
];

// Gather every visible content tab's considerations (heading + bullets) for a
// section, plus the per-marker About descriptions. Categories are ordered the
// same way the tab orders them (prioritizeRootCauseCategories), so the digest
// reads exactly like the tab. Only tabs that actually have content are returned.
export function buildSectionDigest(
  section: Section,
  bloodworkAnalysis: BloodworkAnalysis,
  furtherConsiderations: FurtherConsiderations,
  panelMarkers: PanelMarker[],
): DigestTab[] {
  if (!bloodworkAnalysis?.bloodwork || !furtherConsiderations) return [];

  const categories = prioritizeRootCauseCategories(
    section,
    bloodworkAnalysis.possibleRootCauseCategoriesFor?.(section) ?? [],
    bloodworkAnalysis,
  );
  const tabs: DigestTab[] = [];

  // Blood Clotting & Flow's Insights tab is a computed viscosity card, not a
  // FurtherConsiderations "Interpretation" row, so gather it explicitly. Kept
  // first so it reads into Notes ahead of Possible Root Causes, like the tabs.
  if (section === Section.PLATELETS) {
    // Guard: a compute() throw (viscosity value present but hct/protein absent)
    // must not take down the whole digest — just skip the viscosity card.
    let viscosityInsight = null;
    try {
      viscosityInsight = ViscosityInsights.digestGroup(bloodworkAnalysis);
    } catch (e) {
      viscosityInsight = null;
    }
    if (viscosityInsight) {
      tabs.push({
        key: 'insights',
        name: 'Insights',
        groups: [viscosityInsight],
      });
    }
  }

  CONTENT_TABS.forEach(def => {
    if (!def.show(section)) return;
    const groups: DigestGroup[] = [];
    const seen = new Set<string>();
    categories.forEach(category => {
      const considerations =
        furtherConsiderations.findConsiderations(
          category.rootCauseCategory,
          section,
          def.tab,
        ) || [];
      considerations.forEach(c => {
        // inlineHoverText folds any "[Hover text: …]" explainer into the
        // sentence as a parenthetical — the digest is plain text a
        // practitioner pastes into their note, so it can neither hover nor
        // show the raw token. stripNavTokens does the same for a
        // "[[nav:Section:tab|Label]]" cross-section pointer, leaving the label
        // so the sentence still names the section it sends the reader to.
        const items = (c.items || [])
          .map(it => stripNavTokens(inlineHoverText((it ?? '').trim())))
          .filter(Boolean);
        if (!items.length) return;
        const title = (c.title ?? '').trim();
        // De-dupe identical considerations returned under more than one category
        // (keep genuinely-different groups, even if a bullet repeats elsewhere,
        // so each marker/pattern still shows its own list).
        const sig = `${title}||${items.join('|')}`;
        if (seen.has(sig)) return;
        seen.add(sig);
        groups.push({ title, items });
      });
    });
    if (groups.length) tabs.push({ key: def.key, name: def.name, groups });
  });

  // About — one group per marker (marker name heading + its description),
  // de-duped by categoryId (unit variants of the same analyte resolve to one).
  // Placed before References so the plain-language marker descriptions read
  // ahead of the citation list in the digest.
  const aboutGroups: DigestGroup[] = [];
  const seenAbout: { [categoryId: number]: boolean } = {};
  (panelMarkers || [])
    .filter(pm => pm && pm.field && (!pm.hasValue || pm.hasValue()))
    .forEach(pm => {
      const about = furtherConsiderations.findAboutConsideration(
        section,
        pm.field!.id,
      );
      if (!about || !about.items || !about.items.length) return;
      // Skip empties BEFORE marking the categoryId seen — otherwise a
      // whitespace-only variant suppresses the real description for the same
      // analyte (which a later marker would have supplied).
      const items = about.items.map(s => (s ?? '').trim()).filter(Boolean);
      if (!items.length) return;
      if (seenAbout[about.categoryId]) return;
      seenAbout[about.categoryId] = true;
      aboutGroups.push({ title: (about.title ?? '').trim(), items });
    });
  if (aboutGroups.length) tabs.push({ key: 'about', name: 'About', groups: aboutGroups });

  // References — its items are Reference Library tag slugs; resolve each to
  // plain-text citation form (referenceItemToText) so they read like the tab and
  // give the inline "(n)" markers in the Causes text a home. Pushed last so the
  // citation list sits below the About block.
  //
  // Shares its section list with the References tab itself (see
  // reference-tab-sections.ts). They were two hand-kept copies, and a section
  // present in one but not the other reads as a tab the digest silently drops.
  // A section with no References rows still shows no card, because the loop
  // below only pushes groups it actually finds.
  if (sectionHasReferencesTab(section)) {
    const refGroups: DigestGroup[] = [];
    const seenRef = new Set<string>();
    categories.forEach(category => {
      const refs =
        furtherConsiderations.findConsiderations(
          category.rootCauseCategory,
          section,
          'References',
        ) || [];
      refs.forEach(c => {
        // Same per-row rule the tab applies: only a fully numbered row keeps
        // its "(n)" markers.
        const showNumbers = isNumberedReferenceList(c.items || []);
        const ordered = showNumbers
          ? sortByReferenceNumber(c.items || [])
          : c.items || [];
        const items = ordered
          .map(it =>
            referenceItemToText(it, furtherConsiderations, showNumbers).trim(),
          )
          .filter(Boolean);
        if (!items.length) return;
        const title = (c.title ?? '').trim();
        const sig = `${title}||${items.join('|')}`;
        if (seenRef.has(sig)) return;
        seenRef.add(sig);
        refGroups.push({ title, items });
      });
    });
    if (refGroups.length) tabs.push({ key: 'references', name: 'References', groups: refGroups });
  }

  return tabs;
}

export function tabItemCount(tab: DigestTab): number {
  return tab.groups.reduce((n, g) => n + g.items.length, 0);
}

const hasHeadings = (tab: DigestTab): boolean =>
  tab.groups.some(g => g.title.length > 0);

// One tab as text. When the tab's considerations carry their own headings
// (Insights patterns, per-marker root causes, About markers) each heading is
// kept with its bullets; when they don't, the tab name is used as the single
// heading so the block still says where it came from. `bold` wraps headings in
// ** markers for Insert (the note renders inline markdown); Copy passes plain.
// Both forms run the text through trianglesToArrows: the tab shows ▲/▼ as
// FontAwesome arrow icons, but neither the textarea nor the clipboard can hold
// an icon, so the arrows go in as ↑/↓ characters instead of raw triangles.
function tabToText(tab: DigestTab, bold: boolean): string {
  const head = (t: string) => {
    const arrowed = trianglesToArrows(t);
    return bold ? `**${arrowed}**` : arrowed;
  };
  const bullets = (items: string[]) =>
    items.map(i => `• ${trianglesToArrows(i)}`).join('\n');
  if (!hasHeadings(tab)) {
    const items = tab.groups.reduce<string[]>((acc, g) => acc.concat(g.items), []);
    return `${head(`${tab.name}:`)}\n${bullets(items)}`;
  }
  return tab.groups
    .map(g => (g.title ? `${head(g.title)}\n${bullets(g.items)}` : bullets(g.items)))
    .join('\n');
}

// Plain-text form — used for Copy (goes to the system clipboard, so no markdown
// markers that would look odd pasted into a non-markdown target).
export function digestTabToText(tab: DigestTab): string {
  return tabToText(tab, false);
}

// Markdown form — used for Insert into the note, where the field renders inline
// markdown, so headings are bolded.
export function digestTabToMarkdown(tab: DigestTab): string {
  return tabToText(tab, true);
}

function copyToClipboard(text: string) {
  if (navigator.clipboard && navigator.clipboard.writeText) {
    navigator.clipboard.writeText(text).catch(() => fallbackCopy(text));
  } else {
    fallbackCopy(text);
  }
}
function fallbackCopy(text: string) {
  const el = document.createElement('textarea');
  el.value = text;
  el.style.position = 'fixed';
  el.style.opacity = '0';
  document.body.appendChild(el);
  el.select();
  try {
    document.execCommand('copy');
  } catch (_) {
    /* ignore */
  }
  document.body.removeChild(el);
}

// Split the tabs into two balanced columns (greedy by item count) so the cards
// sit side by side and pack tightly — e.g. a section with just Possible Root
// Causes + About puts About to the RIGHT of Root Causes, not below it.
function splitColumns(tabs: DigestTab[]): [DigestTab[], DigestTab[]] {
  const left: DigestTab[] = [];
  const right: DigestTab[] = [];
  let lh = 0;
  let rh = 0;
  tabs.forEach(tab => {
    const h = tabItemCount(tab) + tab.groups.length; // items + headings ≈ height
    if (lh <= rh) {
      left.push(tab);
      lh += h;
    } else {
      right.push(tab);
      rh += h;
    }
  });
  return [left, right];
}

type Props = {
  tabs: DigestTab[];
  // Keys of tabs whose block is currently present in the note (drives the
  // Insert ⇄ Added toggle). Owned by the parent so it stays in sync with edits.
  insertedKeys: string[];
  onToggleTab: (tab: DigestTab) => void;
  // Insert one section (heading + all its bullets).
  onInsertGroup: (heading: string, items: string[]) => void;
  // Insert one bullet; `heading` is carried in so the note keeps its context.
  onInsertLine: (item: string, heading: string) => void;
  onInsertAll: () => void;
};

type State = { copiedKey: string | null };

// The on-screen "gather from tabs" digest shown below the Save button in a
// section's Notes tab. Every content tab is shown in full at once (no
// expanding) so the practitioner can read everything and pull what they want
// into the note without clicking between tabs.
export class SectionNotesDigest extends React.Component<Props, State> {
  state: State = { copiedKey: null };
  private copyTimer: any = null;

  componentWillUnmount() {
    if (this.copyTimer) clearTimeout(this.copyTimer);
  }

  private flashCopied = (key: string, text: string) => {
    copyToClipboard(text);
    this.setState({ copiedKey: key });
    if (this.copyTimer) clearTimeout(this.copyTimer);
    this.copyTimer = setTimeout(() => this.setState({ copiedKey: null }), 1500);
  };

  private copyAllText = (): string =>
    this.props.tabs.map(digestTabToText).join('\n\n');

  private renderCard = (tab: DigestTab) => {
    const { insertedKeys, onToggleTab, onInsertGroup, onInsertLine } = this.props;
    const { copiedKey } = this.state;
    const isAdded = insertedKeys.indexOf(tab.key) !== -1;
    const count = tabItemCount(tab);
    return (
      <div
        key={tab.key}
        style={{
          border: '1px solid #dee2e6',
          borderRadius: 6,
          backgroundColor: '#fff',
          overflow: 'hidden',
          marginBottom: 12,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '8px 10px',
            backgroundColor: '#f8f9fa',
            borderBottom: '1px solid #dee2e6',
          }}
        >
          <span style={{ fontWeight: 600, fontSize: 13 }}>{tab.name}</span>
          <span style={{ fontSize: 11, color: '#6c757d' }}>
            {count} item{count === 1 ? '' : 's'}
          </span>
          <span style={{ flex: 1 }} />
          <Button
            type="button"
            size="sm"
            variant="link"
            style={{ padding: '0 6px', fontSize: 12 }}
            onClick={() => this.flashCopied(tab.key, digestTabToText(tab))}
            title="Copy this tab"
          >
            {copiedKey === tab.key ? 'Copied' : 'Copy'}
          </Button>
          <Button
            type="button"
            size="sm"
            variant={isAdded ? 'success' : 'outline-primary'}
            style={{ fontSize: 12 }}
            onClick={() => onToggleTab(tab)}
            title={isAdded ? 'Remove this tab from the note' : 'Insert this tab into the note'}
          >
            {isAdded ? '✓ Added' : 'Insert'}
          </Button>
        </div>
        <div style={{ padding: '4px 10px 8px' }}>
          {tab.groups.map((group, gi) => {
            // The heading carried onto every bullet/section from this group:
            // its own title, or the tab name when the group is untitled.
            const heading = group.title || tab.name;
            return (
              <div key={gi}>
                {group.title && (
                  <div style={{ fontWeight: 700, fontSize: 12.5, margin: '8px 0 2px' }}>
                    {renderArrows(group.title, `gt-${gi}`)}
                    <Button
                      type="button"
                      size="sm"
                      variant="link"
                      style={{ padding: '0 5px', fontSize: 12, lineHeight: 1 }}
                      onClick={() => onInsertGroup(group.title, group.items)}
                      title="Add this whole section (heading + all its items) to the note"
                    >
                      +
                    </Button>
                  </div>
                )}
                <ul style={{ margin: 0, padding: '2px 0 2px 18px' }}>
                  {group.items.map((item, i) => (
                    <li key={i} style={{ fontSize: 12.5, marginBottom: 3 }}>
                      {renderArrows(item, `${gi}-${i}`)}
                      <Button
                        type="button"
                        size="sm"
                        variant="link"
                        style={{ padding: '0 5px', fontSize: 12, lineHeight: 1 }}
                        onClick={() => onInsertLine(item, heading)}
                        title="Add just this line to the note"
                      >
                        +
                      </Button>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  render() {
    const { tabs, onInsertAll } = this.props;
    const { copiedKey } = this.state;

    if (!tabs || tabs.length === 0) return null;

    const [left, right] = splitColumns(tabs);

    return (
      <div style={{ marginTop: 18, borderTop: '1px solid #dee2e6', paddingTop: 14 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 8,
            marginBottom: 10,
          }}
        >
          <div>
            <strong style={{ fontSize: 14 }}>Pull from this section&rsquo;s tabs</strong>
            <div style={{ fontSize: 12, color: '#6c757d' }}>
              Everything from this section, shown at once. Insert a tab or a
              single line into your note, or copy it.
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button
              type="button"
              size="sm"
              variant="outline-secondary"
              onClick={() => this.flashCopied('__all__', this.copyAllText())}
              title="Copy every tab's content"
            >
              {copiedKey === '__all__' ? 'Copied' : 'Copy all'}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="primary"
              onClick={onInsertAll}
              title="Insert every tab into the note"
            >
              Insert all
            </Button>
          </div>
        </div>

        {/* Two equal side-by-side columns that stretch the full width. Each is an
            independent vertical stack, so cards pack tightly (a short card tucks
            up under the one above it) instead of aligning to a shared row. */}
        <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
          <div style={{ flex: 1, minWidth: 0 }}>{left.map(this.renderCard)}</div>
          <div style={{ flex: 1, minWidth: 0 }}>{right.map(this.renderCard)}</div>
        </div>
      </div>
    );
  }
}
